import React, { useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Tooltip } from '@mui/material';
import { MAX_VOCA_COUNT } from '../../data/common';
import { useRecoilState } from 'recoil';
import { userInfo } from '../../data/atom';
import CheckBox from '../common/CheckBox';
import CheckIc from '../../assets/images/ic_toggle_check_blud.svg';
import UnCheckIc from '../../assets/images/ic_toggle_uncheck_gray.svg';
import MinusIc from '../../assets/images/ic_minus.svg';
import MinusBlueIc from '../../assets/images/ic_minus_blue.svg';
import PlusIc from '../../assets/images/ic_plus.svg';
import PlusBlueIc from '../../assets/images/ic_plus_blue.svg';
import RecordIc from '../../assets/images/ic_record_set.svg';
import RecordDisableIc from '../../assets/images/ic_record_set_disable.svg';
import LockBadgeIc from '../../assets/images/ic_lock_badge.svg';
import AlertModal from '../common/AlertModal';

const btnStyle = {
  width: '16px',
  height: '16px',
  padding: '0',
  color: '#000',
  background: '#CBD5E1',
  fontWeight: '700',
};
const checkBtnStype = {
  ...btnStyle,
  color: '#773DBD',
  background: '#FFF',
  boxShadow: '0px 0px 3px 0px rgba(0, 0, 0, 0.25)',
};

function QuestionTypeRow({
  type,
  vocabularyList,
  onCheckFirstMeaningYN,
  setVocabularyList,
  questionCount,
  setRecordSetModalOpen,
  config,
  setConfig,
}) {
  const [countValue, setCountValue] = useState(type.count);
  const [countAlert, setCountAlert] = useState(null);
  const [tooltipVisible, setTooltipVisible] = useState(null);
  const [user, setUser] = useRecoilState(userInfo);

  // 해당 타입이 체크되어 있는지 여부(ui 변경)
  const isThisTypeChecked = useMemo(() => {
    const target = vocabularyList?.find(
      voca => voca.chosenType === type.type && voca.checked,
    );
    return target ? true : false;
  }, [vocabularyList, type]);

  // 유닛에서 선택하여 노출되는 어휘에 해당 타입이 있는지 여부
  const vocalistHaveThisTypeYN = useMemo(() => {
    let result = false;
    vocabularyList.forEach(voca => {
      const target = voca.problemTypes?.find(t => t.type === type.type);
      if (target) {
        result = true;
      }
    });
    return result;
  }, [vocabularyList, type]);

  // typeList의 count값 변경시 countValue 반영
  useEffect(() => {
    setCountValue(type.count);
  }, [type.count]);

  // 타입 개수 input 핸들러
  const handleTypeCountEnter = typeStr => {
    // 500이상 입력시 경고
    if (countValue > MAX_VOCA_COUNT) {
      setCountAlert('문제 출제는 최대 500개까지 가능합니다.');
      setCountValue(type.count);
      return;
    }
    // 출제가능 문항수 넘을시 경고
    if (countValue > type.maxCount) {
      setCountAlert(`해당 유형의 최대 출제 가능한 문항 수를\n초과했습니다.`);
      setCountValue(type.count);
      return;
    }
    // countValue변경에 따른 vocabularyList 변경
    // 1) countValue가 type.count보다 작을 때: 해당 typer결정한 어휘중 차이만큼 제외
    if (countValue < type.count) {
      const sameTypeVocaIdxs = vocabularyList
        ?.filter(voca => voca.chosenType === typeStr)
        .map(voca => voca.idx);
      const randomIdxs = [];
      while (randomIdxs?.length < countValue) {
        const randomIdx =
          sameTypeVocaIdxs[
            Math.floor(Math.random() * sameTypeVocaIdxs?.length)
          ];
        if (!randomIdxs.includes(randomIdx)) {
          randomIdxs.push(randomIdx);
        }
      }
      const newVocabularyList = vocabularyList.map(voca => {
        if (randomIdxs.includes(voca.idx)) {
          voca.checked = true;
          voca.chosenType = typeStr;
        } else if (
          !randomIdxs.includes(voca.idx) &&
          sameTypeVocaIdxs.includes(voca.idx)
        ) {
          voca.checked = false;
          voca.chosenType = null;
        }
        return voca;
      });
      setVocabularyList(newVocabularyList);
    }
    // 2) countValue와 type.count가 같을 때: 아무동작 안함
    if (countValue === type.count) return;
    // 3) countValue가 type.count보다 클 때: 해당 type 결정한 것 유지하고, 그외 수만큼 추가
    if (countValue > type.count) {
      const sameTypeVocaIdxs = vocabularyList
        ?.filter(
          voca =>
            voca.chosenType === null &&
            voca.shouldShow &&
            voca.problemTypes?.some(t => t.type === typeStr),
        )
        .map(voca => voca.idx);
      const diff = countValue - type.count;
      const randomIdxs = [];
      while (randomIdxs?.length < diff) {
        const randomIdx =
          sameTypeVocaIdxs[
            Math.floor(Math.random() * sameTypeVocaIdxs?.length)
          ];
        if (!randomIdxs.includes(randomIdx)) {
          randomIdxs.push(randomIdx);
        }
      }
      const newVocabularyList = vocabularyList.map(voca => {
        if (randomIdxs.includes(voca.idx)) {
          voca.checked = true;
          voca.chosenType = typeStr;
        }
        return voca;
      });
      setVocabularyList(newVocabularyList);
    }
  };

  // 타입 개수 plus 버튼 핸들러
  const handlePlus = typeStr => {
    // 최대값 넘지 않게 처리
    if (type.count === type.maxCount) return;
    if (questionCount === MAX_VOCA_COUNT) {
      setCountAlert(`문제 출제는 최대 ${MAX_VOCA_COUNT}개까지 가능합니다.`);
      return;
    }
    // 어휘 중 해당 타입으로 결정했거나, 해당 타입을 가진 어휘들 추리기(이미 checked된 어휘는 제외)
    let sameTypeVocas = [];
    if (
      vocabularyList?.find(
        voca => voca.shouldShow && !voca.checked && voca.chosenType === typeStr,
      )
    ) {
      sameTypeVocas = vocabularyList?.filter(
        voca => voca.shouldShow && !voca.checked && voca.chosenType === typeStr,
      );
    } else {
      sameTypeVocas = vocabularyList?.filter(
        voca =>
          voca.shouldShow &&
          !voca.checked &&
          voca.problemTypes?.find(t => t.type === typeStr),
      );
    }
    // 랜덤으로 어휘 하나 선택
    const randomVoca =
      sameTypeVocas[Math.floor(Math.random() * sameTypeVocas?.length)];
    // 선택한 어휘의 checked 바꾸기(vocabularyList의 순서를 바꾸지 않고 checked만 바꾸기)
    const newVocabularyList = vocabularyList.map(voca => {
      if (voca.idx === randomVoca.idx) {
        voca.checked = !voca.checked;
        voca.chosenType = typeStr;
      }
      return voca;
    });
    setVocabularyList(newVocabularyList);
  };

  // 타입 개수 minus 버튼 핸들러
  const handleMinus = typeStr => {
    // 0개 이하로 내려가지 않게 처리
    if (type.count === 0) return;
    // 해당 타입을 가지고 선택된(checked) 어휘들 추리기
    const sameTypeVocas = vocabularyList?.filter(
      voca => voca.shouldShow && voca.checked && voca.chosenType === typeStr,
    );
    // 랜덤으로 어휘 하나 선택
    const randomVoca =
      sameTypeVocas[Math.floor(Math.random() * sameTypeVocas?.length)];
    // 선택한 어휘의 checked 바꾸기(vocabularyList의 순서를 바꾸지 않고 checked만 바꾸기)
    const newVocabularyList = vocabularyList.map(voca => {
      if (voca.idx === randomVoca.idx) {
        voca.checked = false;
        voca.chosenType = null;
      }
      return voca;
    });
    setVocabularyList(newVocabularyList);
  };

  // 타입 개수 0 버튼 핸들러
  const handleZero = typeStr => {
    // 해당 타입을 가지고 선택된(checked) 어휘들 추리기
    const sameTypeVocas = vocabularyList?.filter(
      voca => voca.shouldShow && voca.checked && voca.chosenType === typeStr,
    );
    // 모두 0으로 변경
    const newVocabularyList = vocabularyList.map(voca => {
      if (sameTypeVocas?.find(v => v.idx === voca.idx)) {
        voca.checked = false;
        voca.chosenType = null;
      }
      return voca;
    });
    setVocabularyList(newVocabularyList);
  };

  // 타입 개수 Max 버튼 핸들러
  const handleMax = typeStr => {
    // checked안된 어휘들 중 해당 타입을 가진 어휘들 추리기
    const sameTypeVocas = vocabularyList?.filter(
      voca =>
        voca.shouldShow &&
        !voca.checked &&
        // (voca.chosenType === typeStr ||
        voca.problemTypes?.find(t => t.type === typeStr),
    );
    // 500개 다 채워지면 경고
    if (questionCount === MAX_VOCA_COUNT) {
      setCountAlert(`문제 출제는 최대 ${MAX_VOCA_COUNT}개까지 가능합니다.`);
      return;
    }

    // 몇개 더 추가할지 계산
    // 최대 출제가능 문항수 : maxAvailableCount = MAX_VOCA_COUNT - questionCount(현재 출제수)
    // 현 타입으로 출제할 수 있는 문항들 : currentTypeAvailabelCount = sameTypeVocas.length
    const maxAvailableCount = MAX_VOCA_COUNT - questionCount; // 100
    const currentTypeAvailabelCount = sameTypeVocas?.length; // 200

    let newVocabularyList = [];
    if (maxAvailableCount >= currentTypeAvailabelCount) {
      // sameTypeVocas 모두 checked로 변경
      newVocabularyList = vocabularyList.map(voca => {
        if (sameTypeVocas?.find(v => v.idx === voca.idx)) {
          voca.checked = true;
          voca.chosenType = typeStr;
        }
        return voca;
      });
    } else if (maxAvailableCount < currentTypeAvailabelCount) {
      // vocabularyList에서 sameTypeVocas에 있는 것 중 maxAvailableCount만큼 랜덤으로 할당하기
      const randomIdxs = [];
      while (randomIdxs?.length < maxAvailableCount) {
        const randomIdx =
          sameTypeVocas[Math.floor(Math.random() * sameTypeVocas?.length)].idx;
        if (!randomIdxs.includes(randomIdx)) {
          randomIdxs.push(randomIdx);
        }
      }
      newVocabularyList = vocabularyList.map(voca => {
        if (randomIdxs.includes(voca.idx)) {
          voca.checked = true;
          voca.chosenType = typeStr;
        }
        return voca;
      });
    }
    setVocabularyList(newVocabularyList);
  };

  // 개수가 0이 되면 '첫뜻만 출제' 체크 해제
  useEffect(() => {
    if (countValue === 0) {
      setConfig(prev => {
        return {
          ...prev,
          onlyFirstMeaning: prev.onlyFirstMeaning?.filter(t => t !== type.type),
        };
      });
    }
  }, [countValue, type, setConfig]);

  return (
    <div
      className="border-b border-[#E7E7E7] flex items-center justify-between px-[8px] py-[10px] whitespace-nowrap overflow-hidden"
      style={{
        position: 'relative',
        background: isThisTypeChecked ? '#F2EDF9' : '#FFF',
      }}
    >
      {vocalistHaveThisTypeYN ? (
        <>
          <div className="flex gap-1.5 items-center self-stretch my-auto">
            <div
              type="button"
              className="flex justify-center items-center w-[24px] h-[24px] leading-[24px] rounded-xl bg-slate-300 text-[14px] font-[700]"
              style={{
                background: isThisTypeChecked ? '#773DBD' : '#CBD5E1',
                color: isThisTypeChecked ? '#FFF' : '#1E293B',
              }}
            >
              {type.type}
            </div>
            <div className="flex gap-1 items-center my-auto">
              <div
                style={{ lineHeight: '20px' }}
                className="whitespace-nowrap leading-[20px] shrink-0 flex w-[20px] min-w-[20px] h-[20px] text-[11px] font-[400] text-[#773DBD] justify-center items-center border border-[#773DBD] border-solid rounded-2xl min-h-[20px]"
              >
                {type.type > 'G' ? '객' : '주'}
              </div>
              <div
                className="self-stretch my-auto text-sm leading-none text-slate-800"
                style={
                  isThisTypeChecked ? { color: '#773DBD', fontWeight: 700 } : {}
                }
              >
                {type.title}
              </div>
              {(type.type === 'B' ||
                type.type === 'C' ||
                type.type === 'G' ||
                type.type === 'H' ||
                type.type === 'I' ||
                type.type === 'J' ||
                type.type === 'K' ||
                type.type === 'M') && (
                <CheckBox
                  id={type.type}
                  value={type.type}
                  isChecked={
                    config.onlyFirstMeaning?.includes(type.type) ? true : false
                  }
                  onChange={onCheckFirstMeaningYN}
                  checkedIcon={CheckIc}
                  uncheckedIcon={UnCheckIc}
                  disabled={!isThisTypeChecked}
                />
              )}
              {type.type === 'F' && (
                <button
                  type="button"
                  onClick={() => setRecordSetModalOpen(true)}
                  disabled={!isThisTypeChecked}
                >
                  {isThisTypeChecked ? (
                    <img src={RecordIc} alt="record" />
                  ) : (
                    <img src={RecordDisableIc} alt="record-disable" />
                  )}
                </button>
              )}
            </div>
          </div>
          <div
            className="relative flex gap-1 items-center self-stretch my-auto"
            style={{ position: 'relative' }}
          >
            <Button
              variant="gray"
              sx={isThisTypeChecked ? checkBtnStype : btnStyle}
              className="flex justify-center items-center w-4 h-4 text-xs font-bold leading-none text-center whitespace-nowrap rounded-sm bg-slate-300 min-h-[16px] text-slate-800"
              onClick={() => handleZero(type.type)}
            >
              <span>0</span>
            </Button>
            <Button
              variant="gray"
              sx={isThisTypeChecked ? checkBtnStype : btnStyle}
              className="flex jutify-center items-center px-1 w-4 h-4 rounded-sm bg-slate-300"
              onClick={() => handleMinus(type.type)}
            >
              {isThisTypeChecked ? (
                <img src={MinusBlueIc} alt="minus" />
              ) : (
                <img src={MinusIc} alt="minus" />
              )}
            </Button>
            <Tooltip
              open={tooltipVisible === type.type}
              title="숫자 입력 후 빈영역을 클릭해 주세요"
              arrow
              placement="top"
            >
              <input
                type="number"
                min={0}
                max={type.maxCount}
                value={countValue}
                onChange={e => setCountValue(e.target.value)}
                className="text-slate-300 w-[29px] min-w-[29px] max-w-[38px] bg-transparent text-right"
                style={{
                  outline: 'none',
                  color:
                    type.count === 0 || type.count === ''
                      ? '#CBD5E1'
                      : '#1E293B',
                }}
                onFocus={() => {
                  setCountValue(prev => (prev === 0 ? '' : prev));
                  if (!user.hasReadTooltip) {
                    setTooltipVisible(type.type);
                  }
                }}
                onBlur={() => {
                  handleTypeCountEnter(type.type); // 빈화면 클릭시 변경된 countValue 적용
                  setCountValue(prev => (prev === '' ? 0 : prev));
                  setTooltipVisible(null);
                  setUser(prev => ({ ...prev, hasReadTooltip: true }));
                }}
              />
            </Tooltip>
            <div className="self-stretch py-0.5 my-auto text-base leading-none whitespace-nowrap text-slate-800">
              /
            </div>
            <div className="text-start gap-2.5 w-[38px] min-w-[38px] max-w-[38px] py-0.5 my-auto text-base leading-none whitespace-nowrap text-slate-800">
              {type.maxCount}
            </div>
            <Button
              variant="gray"
              sx={isThisTypeChecked ? checkBtnStype : btnStyle}
              className="flex justify-center items-center w-4 h-4 rounded-sm"
              onClick={() => handlePlus(type.type)}
            >
              {isThisTypeChecked ? (
                <img src={PlusBlueIc} alt="plus" />
              ) : (
                <img src={PlusIc} alt="plus" />
              )}
            </Button>
            <Button
              variant="gray"
              sx={isThisTypeChecked ? checkBtnStype : btnStyle}
              className="flex justify-center items-center w-4 h-4 text-xs font-bold leading-none text-center whitespace-nowrap rounded-sm bg-slate-300 min-h-[16px] text-slate-800"
              onClick={() => handleMax(type.type)}
            >
              <span>M</span>
            </Button>
          </div>
        </>
      ) : (
        <>
          {(type.type === 'G' || type.type === 'M') && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1,
                pointerEvents: 'all',
                cursor: 'not-allowed',
              }}
            />
          )}
          <div className="flex gap-1.5 items-center self-stretch my-auto">
            <div style={{ position: 'relative' }}>
              <div className="text-[#A9B6CA] border border-[#A9B6CA] flex justify-center items-center w-[24px] h-[24px] rounded-xl bg-[white] text-[14px] font-[700]">
                {type.type}
              </div>
              {(type.type === 'G' || type.type === 'M') && (
                <img
                  src={LockBadgeIc}
                  alt="lock"
                  style={{
                    position: 'absolute',
                    right: '-4px',
                    bottom: '-3px',
                    width: '14px',
                    height: '14px',
                  }}
                />
              )}
            </div>
            <div className="flex gap-1 items-center self-stretch my-auto">
              <div
                style={{ lineHeight: '20px' }}
                className="w-[20px] h-[20px] min-h-[20px] text-[11px] font-[400] text-[#A9B6CA] flex justify-center items-center whitespace-nowrap rounded-2xl border border-[#A9B6CA] border-solid"
              >
                {type.type > 'G' ? '객' : '주'}
              </div>
              <div className="self-stretch my-auto text-sm leading-none text-[#A9B6CA]">
                {type.title}
              </div>
              <CheckBox
                value={type.type}
                checkedIcon={CheckIc}
                uncheckedIcon={UnCheckIc}
                disabled
                isChecked={false}
                onChange={() => {}}
              />
            </div>
          </div>
          {(type.type === 'G' || type.type === 'M') ? (
            <div
              className="flex gap-1 py-[1px] px-[7px] items-center self-stretch my-auto text-[12px] font-[500]"
              style={{ borderRadius: '11px', background: 'rgb(230, 232, 238)', color: '#A9B6CA', border: '1px solid rgb(230, 232, 238)' }}
            >
              추후 제공 예정
            </div>
          ) : (
            <div className="flex gap-1 py-[1px] px-[3px] border border-[#A9B6CA] items-center self-stretch my-auto text-[12px] text-[#A9B6CA] font-[500]">
              출제가능한 문항이 없습니다
            </div>
          )}
        </>
      )}
      <AlertModal
        open={countAlert !== null ? true : false}
        setOpen={setCountAlert}
        content={countAlert}
        btnText="확인"
        onConfirm={() => setCountAlert(null)}
      />
    </div>
  );
}

QuestionTypeRow.propTypes = {
  type: PropTypes.object.isRequired,
  vocabularyList: PropTypes.array.isRequired,
  onCheckFirstMeaningYN: PropTypes.func.isRequired,
  setVocabularyList: PropTypes.func.isRequired,
  questionCount: PropTypes.number.isRequired,
  setRecordSetModalOpen: PropTypes.func.isRequired,
  config: PropTypes.object.isRequired,
  setConfig: PropTypes.func.isRequired,
};

export default React.memo(QuestionTypeRow);
